import React, { useRef, useEffect, useState } from "react";
import CountUp from "react-countup";
import { Box } from "@mui/material";
import {
  PieChart,
  pieArcLabelClasses,
  pieArcClasses,
} from "@mui/x-charts/PieChart";
import { useInView } from "../hooks/useInView";

const StatisticsSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { threshold: 0.4 });

  const [totalFiles, setTotalFiles] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [topContributors, setTopContributors] = useState([]);
  const [labelData, setLabelData] = useState([]);
  const [sourcesData, setSourcesData] = useState([]);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      const response = await fetch(
        "https://getstatistics-umtiunxwoa-ey.a.run.app",
        {
          method: "GET",
        }
      );
      const data = await response.json();
      if (!response.ok) {
        console.error(data);
        return;
      }

      const { totalFiles, totalDuration, topContributorNames, percentLabeled } =
        data;
      setTotalFiles(totalFiles);
      setTotalDuration(Math.floor(totalDuration / 3600));
      setTopContributors(
        topContributorNames.map((contributor) => contributor.name)
      );

      const labelData = [
        { id: 1, value: percentLabeled, label: "Labeled", color: "#C9179A" },
        {
          id: 2,
          value: 100 - percentLabeled,
          label: "Unlabeled",
          color: "#6E3224",
        },
      ];
      setLabelData(labelData);

      const colors = ["#C9179A", "#ed15b4", "#bd1e93", "#9e247e"];
      const sourcesData = [];
      topContributorNames.slice(0, 3).forEach((contributor) => {
        if (contributor.percentage > 0) {
          sourcesData.push({
            id: sourcesData.length,
            value: contributor.percentage,
            label: contributor.name,
            color: colors[sourcesData.length],
          });
        }
      });
      setSourcesData(sourcesData);
    } catch (error) {
      console.log("Error fetching statistics.");
    }
  };

  return (
    <section ref={sectionRef} style={{ textAlign: "center", marginTop: 128 }}>
      <h2 style={{ fontSize: 28, marginBottom: 16 }}>Database Statistics</h2>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
        }}
      >
        {/* Left: CountUp stats */}
        <div
          style={{
            borderRight: "0.5px solid #563A9C",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            paddingRight: 32,
            marginRight: 32,
          }}
        >
          <span style={{ textAlign: "right" }}>
            <span style={{ fontSize: 32 }}>
              {isInView ? (
                <CountUp start={0} end={totalFiles} duration={2} useEasing />
              ) : (
                0
              )}
            </span>{" "}
            <span style={{ fontSize: 16 }}>files</span>
          </span>
          <span style={{ textAlign: "right" }}>
            <span style={{ fontSize: 32 }}>
              {isInView ? (
                <CountUp start={0} end={totalDuration} duration={2} useEasing />
              ) : (
                0
              )}
            </span>{" "}
            <span style={{ fontSize: 16 }}>hours</span>
          </span>
        </div>

        {/* Right: Pie Charts */}
        <div
          style={{
            display: "flex",
            gap: 32,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box sx={{ width: 150, height: 150 }}>
            {isInView && (
              <PieChart
                series={[
                  {
                    data: sourcesData,
                    arcLabel: "label",
                    highlightScope: { fade: "global", highlight: "item" },
                  },
                ]}
                width={250}
                height={150}
                slotProps={{
                  legend: { hidden: true },
                  popper: {
                    sx: {
                      "& .MuiChartsTooltip-cell": {
                        color: "#6E3224 !important",
                        fontFamily: "Roboto Serif !important",
                        backgroundColor: "#E6D3A2 !important",
                      },
                      "& .MuiChartsTooltip-mark": {
                        border: "0px !important",
                        width: "10px !important",
                        height: "10px !important",
                      },
                    },
                  },
                }}
                sx={{
                  [`& .${pieArcLabelClasses.root}`]: {
                    fontSize: 11,
                    fill: "#E6D3A2",
                    fontFamily: "Roboto Serif",
                  },
                  [`& .${pieArcClasses.root}`]: {
                    stroke: "none",
                  },
                }}
              />
            )}
            <div style={{ marginTop: 8 }}>Sources</div>
          </Box>
          <Box sx={{ width: 150, height: 150 }}>
            {isInView && (
              <PieChart
                series={[
                  {
                    data: labelData,
                    arcLabel: "label",
                    highlightScope: { fade: "global", highlight: "item" },
                    arcLabelMinAngle: 40,
                  },
                ]}
                width={250}
                height={150}
                slotProps={{
                  legend: { hidden: true },
                  popper: {
                    sx: {
                      "& .MuiChartsTooltip-cell": {
                        color: "#6E3224 !important",
                        fontFamily: "Roboto Serif !important",
                        backgroundColor: "#E6D3A2 !important",
                      },
                      "& .MuiChartsTooltip-mark": {
                        border: "0px !important",
                        width: "10px !important",
                        height: "10px !important",
                      },
                    },
                  },
                }}
                sx={{
                  [`& .${pieArcLabelClasses.root}`]: {
                    fontSize: 11,
                    fill: "#E6D3A2",
                    fontFamily: "Roboto Serif",
                  },
                  [`& .${pieArcClasses.root}`]: {
                    stroke: "none",
                  },
                }}
              />
            )}
            <div style={{ marginTop: 8 }}>Labeled</div>
          </Box>
        </div>
      </div>

      <h3 style={{ fontSize: 28, marginTop: 128 }}>Top Contributors</h3>
      <p
        style={{
          textAlign: "center",
          maxWidth: 800,
          margin: "auto",
          paddingLeft: 32,
          paddingRight: 32,
          marginTop: 8,
        }}
      >
        {topContributors.map((contributor, index) => (
          <span key={index}>
            {contributor}
            {index < topContributors.length - 1 ? ", " : ""}
          </span>
        ))}
      </p>

      {false && (
        <div>
          <h3 style={{ fontSize: 28, marginTop: 128 }}>Our Partners</h3>
          <img src="partners.png" style={{ maxWidth: 800, margin: "auto" }} />
        </div>
      )}
    </section>
  );
};

export default StatisticsSection;
